import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import { useSocketContext } from "../context/SocketContext";
import { getSocketWorkspace } from "../utils/socketWorkspace";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { buildTeamPresenceXpro4Payload } from "../utils/teamPresenceTelemetry";
import { rememberLocalPresenceEmit } from "../utils/teamPresenceUsersSocket";
import { postPresenceClientEnv } from "../api/client";
import { hasActiveTenantWorkspaceContext, isPartnerConsolePath } from "../utils/partnerPortal";
import { resolvePresenceUserId } from "../utils/presenceIdentityFromAuth";

const EMIT_INTERVAL_MS_VISIBLE = 20_000;
const EMIT_INTERVAL_MS_HIDDEN = 45_000;

/**
 * Emite `users` com telemetria Xpro 4 em **toda** aba autenticada, em qualquer rota.
 * O hook `useTeamSocketPresence` só escuta — sem isso, só a aba que montava o hook (ex.: dashboard) aparecia no socket.
 */
export default function TeamPresenceSocketEmitter() {
  const { socket } = useSocketContext();
  const { userData, bearerToken } = useAuthContext();
  const location = useLocation();
  const [emitIntervalMs, setEmitIntervalMs] = useState(EMIT_INTERVAL_MS_VISIBLE);
  const tenantActive = hasActiveTenantWorkspaceContext();
  const onPartnerConsole = isPartnerConsolePath(location.pathname);

  useEffect(() => {
    const onVis = () => setEmitIntervalMs(document.hidden ? EMIT_INTERVAL_MS_HIDDEN : EMIT_INTERVAL_MS_VISIBLE);
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    if (!socket || !tenantActive || onPartnerConsole) return undefined;

    const emitUsers = () => {
      let workspace = getSocketWorkspace();
      if (!workspace) {
        const company = getActiveWorkspaceSlug();
        if (company) workspace = `${company}.xpro.me`;
      }
      const id = resolvePresenceUserId(bearerToken, userData);
      if (!workspace || id == null) return;
      const name = userData?.nome || userData?.name || "";
      const payload = buildTeamPresenceXpro4Payload({ id, name, workspace });
      rememberLocalPresenceEmit({ ...payload, socketId: socket.id });
      socket.emit("users", payload);
      postPresenceClientEnv({
        workspace,
        tabSessionId: payload.tab,
        path: payload.path,
        since: payload.since,
        lastActivityAt: payload.lastActivityAt,
        focused: payload.focused,
        deviceType: payload.deviceType,
        browserName: payload.browserName,
        appVersion: payload.appVersion,
        appTheme: payload.appTheme,
      }).catch(() => {});
    };

    emitUsers();
    const interval = setInterval(emitUsers, emitIntervalMs);
    return () => clearInterval(interval);
  }, [socket, bearerToken, userData?.id, userData?.nome, userData?.name, emitIntervalMs, tenantActive, onPartnerConsole]);

  return null;
}
